/**
 * Localization strings used in top menu and user tools
 *
 * @module
 */

import { createDictionary } from './utils';

// Refer to "Guidelines for Programmers" in ./README.md before editing this file

export const headerText = createDictionary({
  skipToContent: {
    comment: 'For accessibility purposes. Allows jumping to page content',
    'en-us': 'Skip to Content',
    'ru-ru': 'Перейти к содержанию',
    'es-es': 'Ir al contenido',
    'fr-fr': 'Passer au contenu',
    'uk-ua': 'Перейти до вмісту',
    'de-ch': 'Weiter zum Inhalt',
    'pt-br': 'Ir para o conteúdo',
    'hr-hr': 'Preskoči na sadržaj',
    nb: 'Gå til innhold',
  },
  main: {
    comment: 'As in "main menu"',
    'en-us': 'Main',
    'es-es': 'Principal',
    'ru-ru': 'Основной',
    'uk-ua': 'Головна',
    'de-ch': 'Allgemein',
    'fr-fr': 'Principal',
    'pt-br': 'Principal',
    'hr-hr': 'Glavni',
    nb: 'Hoved',
  },
  currentCollection: {
    comment: 'Example: Current Collection',
    'en-us': 'Current {collectionTable:string}',
    'ru-ru': 'Текущая {collectionTable:string}',
    'es-es': 'Actual {collectionTable:string}',
    'fr-fr': 'Actuelle{collectionTable:string}',
    'uk-ua': 'Поточна {collectionTable:string}',
    'de-ch': 'Derzeitige {collectionTable:string}',
    'pt-br': 'Atual {collectionTable:string}',
    'hr-hr': 'Trenutni {collectionTable:string}',
    nb: 'Gjeldende {collectionTable:string}',
  },
  dataEntry: {
    'en-us': 'Data Entry',
    'ru-ru': 'Ввод данных',
    'es-es': 'Entrada de datos',
    'fr-fr': 'Saisie des données',
    'uk-ua': 'Введення даних',
    'de-ch': 'Datenerfassung',
    'pt-br': 'Entrada de dados',
    'hr-hr': 'Unos podataka',
    nb: 'Dataregistrering',
  },
  makeDwca: {
    'en-us': 'Create DwC Archive',
    'ru-ru': 'Создать архив DwC',
    'es-es': 'Crear un archivo DwC',
    'fr-fr': 'Créer une archive DwC',
    'uk-ua': 'Створити архів DwC',
    'de-ch': 'DwC-Archiv erstellen',
    'pt-br': 'Criar arquivo DwC',
    'hr-hr': 'Izradi DwC arhivu',
    nb: 'Opprett DwC-arkiv',
  },
  backupDatabase: {
    'en-us': 'Backup Database',
    'ru-ru': 'Резервное копирование базы данных',
    'es-es': 'Copia de seguridad de la base de datos',
    'fr-fr': 'Sauvegarder la base de données',
    'uk-ua': 'Резервне копіювання бази даних',
    'de-ch': 'Datenbank sichern',
    'pt-br': 'Fazer backup do banco de dados',
    'hr-hr': 'Sigurnosna kopija baze podataka',
    nb: 'Sikkerhetskopiering av database',
  },
  updateExportFeed: {
    'en-us': 'Update RSS Feed',
    'ru-ru': 'Обновить RSS-канал',
    'es-es': 'Actualizar feed RSS',
    'fr-fr': 'Mettre à jour le flux RSS',
    'uk-ua': 'Оновити RSS-канал',
    'de-ch': 'RSS-Feed aktualisieren',
    'pt-br': 'Atualizar feed RSS',
    'hr-hr': 'Ažuriranje RSS feeda',
    nb: 'Oppdater RSS-strøm',
  },
  updateExportFeedConfirmation: {
    'en-us': 'Update export feed?',
    'ru-ru': 'Обновить фид экспорта?',
    'es-es': '¿Actualizar el feed de exportación?',
    'fr-fr': "Mettre à jour le flux d'exportation ?",
    'uk-ua': 'Оновити канал експорту?',
    'de-ch': 'Export-Feed aktualisieren?',
    'pt-br': 'Atualizar feed de exportação?',
    'hr-hr': 'Ažurirati feed za izvoz?',
    nb: 'Oppdatere eksport-strømmen?',
  },
  updateExportFeedConfirmationDescription: {
    'en-us': 'Update all RSS export feed items now?',
    'ru-ru': 'Обновить все элементы RSS-канала экспорта сейчас?',
    'es-es':
      '¿Actualizar todos los elementos del feed de exportación RSS ahora?',
    'fr-fr':
      "Mettre à jour tous les éléments du flux d'exportation RSS maintenant ?",
    'uk-ua': 'Оновити всі елементи RSS-експорту?',
    'de-ch': 'Jetzt alle RSS-Export-Feed-Elemente aktualisieren?',
    'pt-br':
      'Agora é necessário atualizar todos os itens do feed RSS exportado?',
    'hr-hr': 'Ažurirati sve stavke RSS izvoza feeda sada?',
    nb: 'Oppdatere alle RSS-eksportstrømelementer nå?',
  },
  feedExportStarted: {
    'en-us': 'Export feed update started',
    'ru-ru': 'Начато обновление фида экспорта',
    'es-es': 'Iniciada la actualización del feed de exportación',
    'fr-fr': "La mise à jour du flux d'exportation a commencé",
    'uk-ua': 'Оновлення каналу експорту розпочато',
    'de-ch': 'Aktualisierung des Export-Feeds gestartet',
    'pt-br': 'Atualização do feed de exportação iniciada',
    'hr-hr': 'Započeto je ažuriranje feeda izvoza',
    nb: 'Oppdatering av eksportstrøm har startet',
  },
  feedExportStartedDescription: {
    'en-us':
      'Update started. You will receive a notification for each feed item updated.',
    'ru-ru':
      'Обновление запущено. Вы будете получать уведомление о каждом обновлении элемента фида.',
    'es-es':
      'Actualización iniciada. Recibirá una notificación por cada elemento actualizado.',
    'fr-fr':
      'La mise à jour a commencé. Vous recevrez une notification pour chaque élément de flux mis à jour.',
    'uk-ua':
      'Оновлення розпочато. Ви отримаєте сповіщення про кожен оновлений елемент каналу.',
    'de-ch':
      'Aktualisierung gestartet. Sie erhalten eine Benachrichtigung für jedes aktualisierte Feed-Element.',
    'pt-br':
      'Atualização iniciada. Você receberá uma notificação para cada item do feed atualizado.',
    'hr-hr':
      'Ažuriranje je započelo. Primit ćete obavijest za svaku ažuriranu stavku feeda.',
    nb: 'Oppdatering har startet. Du vil motta et varsel for hvert strømelement som er oppdatert.',
  },
  dwcaExportStarted: {
    'en-us': 'DwCA export started',
    'ru-ru': 'Начат экспорт DwCA',
    'es-es': 'Exportación DwCA iniciada',
    'fr-fr': "L'exportation DwCA a démarré",
    'uk-ua': 'Розпочато експорт DwCA',
    'de-ch': 'DwCA-Export gestartet',
    'pt-br': 'A exportação DwCA foi iniciada.',
    'hr-hr': 'Izvoz DwCA-a je započeo',
    nb: 'DwCA-eksport har startet',
  },
  dwcaExportStartedDescription: {
    'en-us':
      'Export started. You will receive a notification when the export is complete.',
    'ru-ru':
      'Экспорт запущен. Вы получите уведомление, когда экспорт будет завершен.',
    'es-es':
      'Exportación iniciada. Recibirá una notificación cuando finalice la exportación.',
    'fr-fr':
      "L'exportation a commencé. Vous recevrez une notification lorsque l'exportation sera terminée.",
    'uk-ua':
      'Експорт розпочато. Коли експорт завершиться, ви отримаєте сповіщення.',
    'de-ch':
      'Export gestartet. Sie erhalten eine Benachrichtigung, sobald der Export abgeschlossen ist.',
    'pt-br':
      'Exportação iniciada. Você receberá uma notificação quando a exportação for concluída.',
    'hr-hr': 'Izvoz je započeo. Primit ćete obavijest kada izvoz bude dovršen.',
    nb: 'Eksporten har startet. Du vil motta et varsel når eksporten er fullført.',
  },
  localityUpdateTool: {
    'en-us': 'Locality Update Tool',
    'de-ch': 'Tool zur Aktualisierung der Ortsinformationen',
    'es-es': 'Herramienta de actualización de localidad',
    'fr-fr': "Outil d'import de coordonnées associées aux objets",
    'ru-ru': 'Инструмент обновления местоположения',
    'uk-ua': 'Інструмент оновлення місцевості',
    'pt-br': 'Ferramenta de atualização de localidade',
    'hr-hr': 'Alat za ažuriranje lokacije',
    nb: 'Verktøy for lokalitetsoppdatering',
  },
  labelName: {
    'en-us': 'Label Name',
    'ru-ru': 'Название ярлыка',
    'es-es': 'Herramientas administrativas',
    'uk-ua': 'Назва бірки',
    'de-ch': 'Etikett Name',
    'fr-fr': 'Outils administratifs',
    'pt-br': 'Nome da etiqueta',
    'hr-hr': 'Naziv oznake',
    nb: 'Etikettnavn',
  },
  reportName: {
    'en-us': 'Report Name',
    'ru-ru': 'Название отчета',
    'es-es': 'Reportar nombre',
    'fr-fr': 'Nom du rapport',
    'uk-ua': 'Назва звіту',
    'de-ch': 'Name des Berichts',
    'pt-br': 'Nome do relatório',
    'hr-hr': 'Naziv izvješća',
    nb: 'Rapportnavn',
  },
  createLabel: {
    'en-us': 'Create new label',
    'ru-ru': 'Создать новый ярлык',
    'es-es': 'Crear nueva etiqueta',
    'fr-fr': 'Créer une nouvelle étiquette',
    'uk-ua': 'Створити нову бірку',
    'de-ch': 'Neues Label erstellen',
    'pt-br': 'Criar nova etiqueta',
    'hr-hr': 'Izradi novu oznaku',
    nb: 'Opprett ny etikett',
  },
  createReport: {
    'en-us': 'Create new report',
    'ru-ru': 'Создать новый отчет',
    'es-es': 'Crear nuevo informe',
    'fr-fr': 'Créer un nouveau document',
    'uk-ua': 'Створити новий звіт',
    'de-ch': 'Neuen Bericht erstellen',
    'pt-br': 'Criar novo relatório',
    'hr-hr': 'Izradi novo izvješće',
    nb: 'Opprett ny rapport',
  },
  repairTree: {
    'en-us': 'Repair Tree',
    'ru-ru': 'Ремонтное дерево',
    'es-es': 'Se ha borrado la caché. Por favor recarga la página.',
    'fr-fr': 'Réparer les arbres',
    'uk-ua': 'Ремонтувати дерево',
    'de-ch': 'Baum reparieren',
    'pt-br': 'Árvore de reparo',
    'hr-hr': 'Popravak stabla',
    nb: 'Reparer treet',
  },
  treeRepairComplete: {
    'en-us': 'Tree repair is complete.',
    'ru-ru': 'Ремонт дерева завершен.',
    'es-es': 'La reparación del árbol está completa.',
    'fr-fr': "La réparation de l'arbre est terminée.",
    'uk-ua': 'Ремонт дерева завершено.',
    'de-ch': 'Die Baumreparatur ist abgeschlossen.',
    'pt-br': 'O reparo da árvore está concluído.',
    'hr-hr': 'Popravak drveća je završen.',
    nb: 'Reparasjonen av treet er fullført.',
  },
  choose: {
    'en-us': 'Choose',
    'de-ch': 'Wählen',
    'es-es': 'Elegir',
    'fr-fr': 'Choisir',
    'ru-ru': 'Выбирать',
    'uk-ua': 'Виберіте',
    'pt-br': 'Escolher',
    'hr-hr': 'Odaberite',
    nb: 'Velg',
  },
  chooseDwca: {
    'en-us': 'Choose DwCA',
    'ru-ru': 'Выберите ДвКА',
    'es-es': 'Elige DwCA',
    'uk-ua': 'Виберіть DwCA',
    'de-ch': 'DwCA wählen',
    'fr-fr': 'Choisissez la DwCA',
    'pt-br': 'Escolha DwCA',
    'hr-hr': 'Odaberite DwCA',
    nb: 'Velg DwCA',
  },
  dwcaResource: {
    'en-us': 'DwCA Resource',
    'de-ch': 'DwCA-Ressource',
    'es-es': 'Recurso DwCA',
    'fr-fr': 'Ressources DwC-A',
    'ru-ru': 'Ресурс DwCA',
    'uk-ua': 'Ресурси DwCA',
    'pt-br': 'Recurso DwCA',
    'hr-hr': 'DwCA resurs',
    nb: 'DwCA-ressurs',
  },
  chooseMetadataResource: {
    'en-us': 'Choose Metadata resource',
    'ru-ru': 'Выберите ресурс метаданных',
    'es-es': 'Elija recurso de metadatos',
    'fr-fr': 'Choisir une ressource de métadonnées',
    'uk-ua': 'Виберіть ресурс метаданих',
    'de-ch': 'Metadaten-Ressource auswählen',
    'pt-br': 'Selecione o recurso de metadados',
    'hr-hr': 'Odaberite resurs metapodataka',
    nb: 'Velg metadataressurs',
  },
  metadataResource: {
    'en-us': 'Metadata Resource',
    'de-ch': 'Metadatenressource',
    'es-es': 'Recurso de metadatos',
    'fr-fr': 'Ressource de métadonnées',
    'ru-ru': 'Ресурс метаданных',
    'uk-ua': 'Ресурс метаданих',
    'pt-br': 'Recurso de metadados',
    'hr-hr': 'Resurs metapodataka',
    nb: 'Metadata-ressurs',
  },
  simpleSearch: {
    'en-us': 'Express Search',
    'ru-ru': 'Экспресс-поиск',
    'es-es': 'Búsqueda sencilla',
    'fr-fr': 'Recherche express',
    'uk-ua': 'Експрес-пошук',
    'de-ch': 'Expresssuche',
    'pt-br': 'Busca expressa',
    'hr-hr': 'Brzo pretraživanje',
    nb: 'Hurtigsøk',
  },
  primarySearch: {
    'en-us': 'Primary Search',
    'ru-ru': 'Основной поиск',
    'es-es': 'Búsqueda primaria',
    'fr-fr': 'Recherche principale',
    'uk-ua': 'Первинний пошук',
    'de-ch': 'Primäre Suche',
    'pt-br': 'Busca principal',
    'hr-hr': 'Primarno pretraživanje',
    nb: 'Primærsøk',
  },
  secondarySearch: {
    'en-us': 'Secondary Search',
    'ru-ru': 'Вторичный поиск',
    'es-es': 'Búsqueda secundaria',
    'fr-fr': 'Recherche secondaire',
    'uk-ua': 'Вторинний пошук',
    'de-ch': 'Sekundäre Suche',
    'pt-br': 'Pesquisa secundária',
    'hr-hr': 'Sekundarno pretraživanje',
    nb: 'Sekundærsøk',
  },
  menuItems: {
    'en-us': 'Menu Items',
    'ru-ru': 'Элементы меню',
    'es-es': 'Elementos de menú',
    'fr-fr': 'Éléments du menu',
    'uk-ua': 'Елементи меню',
    'de-ch': 'Menü-Einträge',
    'pt-br': 'Itens do menu',
    'hr-hr': 'Stavke izbornika',
    nb: 'Menyelementer',
  },
  userTools: {
    'en-us': 'User Tools',
    'ru-ru': 'Инструменты',
    'es-es': 'Herramientas de usuario',
    'fr-fr': 'Outils Utilisateur',
    'uk-ua': 'Інструменти',
    'de-ch': 'Benutzerwerkzeuge',
    'pt-br': 'Ferramentas do usuário',
    'hr-hr': 'Korisnički alati',
    nb: 'Brukerverktøy',
  },
  userToolsForUser: {
    'en-us': 'User Tools ({userName:string})',
    'ru-ru': 'Инструменты ({userName:string})',
    'es-es': 'Herramientas de usuario ({userName:string})',
    'fr-fr': 'Outils Utilisateur ({userName:string})',
    'uk-ua': 'Інструменти ({userName:string})',
    'de-ch': 'Benutzerwerkzeuge ({userName:string})',
    'pt-br': 'Ferramentas do usuário ({userName:string})',
    'hr-hr': 'Korisnički alati ({userName:string})',
    nb: 'Brukerverktøy ({userName:string})',
  },
  helpLocalizeSpecify: {
    'en-us': 'Help Localize Specify 7',
    'ru-ru': 'Помогите локализовать Укажите 7',
    'es-es': 'Ayuda a localizar Especificar 7',
    'fr-fr': 'Aidez à traduire Specify 7',
    'uk-ua': 'Допоможіть перекласти Specify 7',
    'de-ch': 'Hilf beim übersetzen von Specify 7',
    'pt-br': 'Ajuda Localizar Especificar 7',
    'hr-hr': 'Pomoć pri lokalizaciji Navedite 7',
    nb: 'Hjelp Lokaliser Spesifiser 7',
  },
  helpLocalizeSpecifyDescription: {
    'en-us':
      'We would be very grateful for your support localizing Specify 7 User Interface. If you are interested, please <link>see the instructions</link>.',
    'ru-ru':
      'Мы будем очень признательны за вашу поддержку в локализации пользовательского интерфейса Specify 7. Если вы заинтересованы, отправьте электронное письмо по адресу <link>см. Инструкции</link>.',
    'es-es':
      'Estaríamos muy agradecidos por su ayuda para localizar la interfaz de usuario de Specify 7. Si está interesado, por favor <link>consulte las instrucciones</link>.',
    'fr-fr':
      "Nous serions très reconnaissants de votre soutien pour la traduction de l'interface utilisateur Specify 7. Si vous êtes intéressé, veuillez <link>voir les instructions</link>.",
    'uk-ua':
      'Ми будемо дуже вдячні за вашу підтримку в перекладі інтерфейсу Specify 7. Якщо ви зацікавлені, <link>перегляньте інструкції</link>.',
    'de-ch':
      'Wir wären sehr dankbar für Ihre Unterstützung bei der Übersetzung der Specify 7 Benutzeroberfläche. Wenn Sie daran interessiert sind, lesen Sie bitte <link>die Anleitung</link>.',
    'pt-br':
      'Agradecemos muito o seu apoio na localização da Interface de Usuário do Specify 7. Se estiver interessado, por favor, <link>consulte as instruções</link>.',
    'hr-hr':
      'Bili bismo vam vrlo zahvalni na podršci u lokalizaciji korisničkog sučelja Specify 7. Ako ste zainteresirani, molimo <link>pogledajte upute</link>.',
    nb: 'Vi vil sette stor pris på din støtte med å lokalisere Specifice 7-brukergrensesnittet. Hvis du er interessert, kan du <link>se instruksjonene </link>.',
  },
  incompleteInline: {
    'en-us': '(incomplete)',
    'es-es': '(incompleto)',
    'fr-fr': '(incomplet)',
    'ru-ru': '(неполный)',
    'uk-ua': '(не закінчено)',
    'de-ch': '(unvollständig)',
    'pt-br': '(incompleto)',
    'hr-hr': '(nepotpun)',
    nb: '(ufullstendig)',
  },
  incompleteLocalization: {
    'en-us': 'Incomplete localization',
    'es-es': 'Localización incompleta',
    'fr-fr': 'Localisation incomplète',
    'ru-ru': 'Неполная локализация',
    'uk-ua': 'Неповна локалізація',
    'de-ch': 'Unvollständige Übersetzung',
    'pt-br': 'Localização incompleta',
    'hr-hr': 'Nepotpuna lokalizacija',
    nb: 'Ufullstendig lokalisering',
  },
  incompleteLocalizationDescription: {
    'en-us':
      'Translation to this language is not yet complete. Some elements may be missing localization, or have incorrect localization. If you are interested in helping us complete localization, please <link>follow the instructions.</link>',
    'es-es':
      'La traducción a este idioma aún no está completa. Es posible que a algunos elementos les falte localización o que tengan una localización incorrecta. Si está interesado en ayudarnos a completar la localización, <link>siga las instrucciones.</link>',
    'fr-fr':
      "La traduction dans cette langue n'est pas encore terminée. Certains éléments peuvent ne pas être localisés ou avoir une localisation incorrecte. Si vous souhaitez nous aider à terminer la localisation, veuillez <link>suivre les instructions.</link>",
    'ru-ru':
      'Перевод на этот язык еще не завершен. У некоторых элементов может отсутствовать локализация или они имеют неправильную локализацию. Если вы хотите помочь нам завершить локализацию, <link>следуйте инструкциям.</link>',
    'uk-ua':
      'Переклад цією мовою ще не завершено. Деякі елементи можуть не мати локалізації або мати неправильну локалізацію. Якщо ви зацікавлені в тому, щоб допомогти нам завершити локалізацію, будь ласка, <link>прочитайте інструкцій.</link>',
    'de-ch':
      'Die Übersetzung in diese Sprache ist noch nicht abgeschlossen. Bei einigen Elementen fehlt möglicherweise die Lokalisierung oder sie sind nicht korrekt lokalisiert. Wenn Sie daran interessiert sind, uns bei der Vervollständigung der Übersetzung zu helfen, <link>befolgen Sie bitte die Anweisungen.</link>',
    'pt-br':
      'A tradução para este idioma ainda não está completa. Alguns elementos podem estar sem localização ou com localização incorreta. Se você estiver interessado em nos ajudar a concluir a localização, siga as instruções.',
    'hr-hr':
      'Prijevod na ovaj jezik još nije dovršen. Nekim elementima možda nedostaje lokalizacija ili je lokalizacija netočna. Ako ste zainteresirani da nam pomognete dovršiti lokalizaciju, molimo <link>slijedite upute.</link>',
    nb: 'Oversettelsen til dette språket er ikke fullført ennå. Noen elementer kan mangle lokalisering, eller ha feil lokalisering. Hvis du er interessert i å hjelpe oss med å fullføre lokaliseringen, kan du <link> følge instruksjonene.</link>',
  },
  tableApi: {
    'en-us': 'Tables API',
    'ru-ru': 'API таблиц',
    'es-es': 'API de tablas',
    'fr-fr': 'API des tableaux',
    'uk-ua': 'API таблиць',
    'de-ch': 'Tabellen-API',
    'pt-br': 'API de tabelas',
    'hr-hr': 'API za tablice',
    nb: 'Tabeller API',
  },
  operationsApi: {
    'en-us': 'Operations API',
    'ru-ru': 'Операционный API',
    'es-es': 'API de operaciones',
    'fr-fr': 'API des fonctionnalités',
    'uk-ua': 'API операцій',
    'de-ch': 'Operations-API',
    'pt-br': 'API de Operações',
    'hr-hr': 'API za operacije',
    nb: 'Operasjoner API',
  },
  documentation: {
    'en-us': 'Documentation',
    'ru-ru': 'Документация',
    'es-es': 'Documentación',
    'fr-fr': 'Documentation',
    'uk-ua': 'Документація',
    'de-ch': 'Dokumentation',
    'pt-br': 'Documentação',
    'hr-hr': 'Dokumentacija',
    nb: 'Dokumentasjon',
  },
  administration: {
    'en-us': 'Administrative Tools',
    'ru-ru': 'Инструменты управления',
    'es-es': 'Herramientas administrativas',
    'fr-fr': 'Outils Administrateur',
    'uk-ua': 'Адміністрування',
    'de-ch': 'Administrative Werkzeuge',
    'pt-br': 'Ferramentas Administrativas',
    'hr-hr': 'Administrativni alati',
    nb: 'Administrative verktøy',
  },
  developers: {
    'en-us': 'Developer Resources',
    'ru-ru': 'Ресурсы для разработчиков',
    'es-es': 'Recursos para desarrolladores',
    'fr-fr': 'Ressources pour les développeurs',
    'uk-ua': 'Для розробників',
    'de-ch': 'Ressourcen für Entwickler',
    'pt-br': 'Recursos para desenvolvedores',
    'hr-hr': 'Resursi za razvojne programere',
    nb: 'Utvikler-ressurser',
  },
  forum: {
    'en-us': 'Community Forum',
    'ru-ru': 'форум сообщества',
    'es-es': 'Foro Comunitario',
    'fr-fr': 'Forum de la communauté',
    'uk-ua': 'Форум',
    'de-ch': 'Community-Forum',
    'pt-br': 'Fórum da Comunidade',
    'hr-hr': 'Forum zajednice',
    nb: 'Fellesskapsforum',
  },
  clearCache: {
    'en-us': 'Clear Browser Cache',
    'ru-ru': 'Очистить кеш браузера',
    'es-es': 'Borrar caché del navegador',
    'fr-fr': 'Vider le cache du navigateur',
    'uk-ua': 'Очистити кеш браузера',
    'de-ch': 'Browser-Cache leeren',
    'pt-br': 'Limpar o cache do navegador',
    'hr-hr': 'Obriši predmemoriju preglednika',
    nb: 'Tøm nettleserbufferen',
  },
  cacheCleared: {
    'en-us': 'Cache has been cleared. Please reload the page.',
    'ru-ru': 'Кэш очищен. Пожалуйста, перезагрузите страницу.',
    'es-es': 'Se ha borrado la caché. Por favor recarga la página.',
    'fr-fr': 'Le cache a été vidé. Veuillez recharger la page.',
    'uk-ua': 'Кеш очищено. Перезавантажте сторінку.',
    'de-ch': 'Der Cache wurde geleert. Bitte laden Sie die Seite neu.',
    'pt-br': 'O cache foi limpo. Recarregue a página.',
    'hr-hr':
      'Predmemorija je izbrisana. Molimo vas da ponovno učitate stranicu.',
    nb: 'Hurtigbufferen er tømt. Vennligst last inn siden på nytt.',
  },
  technicalDocumentation: {
    'en-us': 'Documentation',
    'ru-ru': 'Документация',
    'es-es': 'Documentación',
    'fr-fr': 'Documentation',
    'uk-ua': 'Документація',
    'de-ch': 'Dokumentation',
    'pt-br': 'Documentação',
    'hr-hr': 'Dokumentacija',
    nb: 'Dokumentasjon',
  },
  chronostratigraphicChart: {
    'en-us': 'Chronostratigraphic Chart',
    'de-ch': 'Chronostratigraphische Tabelle',
    'es-es': 'Cuadro cronoestratigráfico',
    'fr-fr': 'Charte chronostratigraphique',
    'pt-br': 'Carta cronoestratigráfica',
    'ru-ru': 'Хроностратиграфическая диаграмма',
    'uk-ua': 'Хроностратиграфічна діаграма',
    'hr-hr': 'Kronostratigrafski grafikon',
    nb: 'Kronostratigrafisk diagram',
  },
} as const);
